import { ComponentProps, forwardRef, useState } from 'react';
import { PopoverClose } from '@radix-ui/react-popover';
import { CalendarIcon } from 'lucide-react';
import { Matcher } from 'react-day-picker';
import {
  Button,
  Calendar,
  cn,
  containerVariants,
  LoadingIcon,
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@sib/ui';
import { format } from 'date-fns';
import { XIcon } from '../../../icons/XIcon';
import { TimePicker } from './components';

interface ClearButtonProps extends ComponentProps<typeof Button> {
  label: string;
  containerClassName?: string;
}

export type DatetimePickerProps = Omit<
  ComponentProps<typeof Calendar>,
  'selected' | 'onSelect' | 'mode' | 'disabled'
> & {
  withTime?: boolean;
  buttonProps?: ComponentProps<typeof Button>;
  buttonLabel?: string;
  // TODO: handle all possible cases
  selected: Date | null;
  onSelect: (date: Date | null) => void;
  confirmLabel: string;
  getTopLabel?: (selected: DatetimePickerProps['selected']) => string;
  dateFormat?: string;
  dateTimeFormat?: string;
  clearButtonProps?: ClearButtonProps;
  defaultDate?: Date | null;
  closeOnSelect?: boolean;
  disabled?: Matcher | Matcher[] | boolean;
  loading?: boolean;
};

const DEFAULT_HOUR = 17;
const DEFAULT_DATE_FORMAT = 'PPP';
const DEFAULT_DATE_TIME_FORMAT = 'PPP p';

const DatetimePicker = forwardRef<HTMLButtonElement, DatetimePickerProps>(
  (props, ref) => {
    const {
      buttonProps,
      buttonLabel,
      onSelect,
      selected,
      withTime,
      confirmLabel,
      getTopLabel,
      defaultMonth,
      dateTimeFormat = DEFAULT_DATE_TIME_FORMAT,
      dateFormat = DEFAULT_DATE_FORMAT,
      clearButtonProps,
      defaultDate = new Date(),
      closeOnSelect = false,
      disabled,
      loading,
      ...rest
    } = props;

    const [isPopoverOpen, setIsPopoverOpen] = useState(false);
    const [isTimeViewActive, setIsTimeViewActive] = useState(false);

    // TODO: To disscuss with Wojtek.
    const isButtonDisabled = typeof disabled === 'boolean' ? disabled : false;
    const calendarDisabled =
      typeof disabled === 'boolean' ? (disabled ? true : undefined) : disabled;

    const handleTimeChange = (type: 'hour' | 'minute', value: string) => {
      if (selected) {
        const newDate = new Date(selected);

        if (type === 'hour') {
          newDate.setHours(parseInt(value));
        }

        if (type === 'minute') {
          newDate.setMinutes(parseInt(value));
        }

        onSelect(newDate);
      }
    };

    const handleClear = () => {
      onSelect(null);
      setIsTimeViewActive(false);
    };
    const mappedDefaultDate = defaultDate || undefined;

    return (
      <Popover
        open={isPopoverOpen}
        onOpenChange={open => {
          setIsPopoverOpen(open);
          if (!open) setIsTimeViewActive(false);
        }}
      >
        <PopoverTrigger asChild className="w-full">
          <Button
            ref={ref}
            {...buttonProps}
            variant="ghost"
            disabled={buttonProps?.disabled || isButtonDisabled || loading}
            className={cn(
              containerVariants({ variant: 'base' }),
              'items-center justify-start',
              !selected && 'text-muted-foreground',
              buttonProps?.className
            )}
          >
            {loading ? (
              <LoadingIcon className="size-5" />
            ) : (
              <CalendarIcon className="size-5" />
            )}
            {selected ? (
              format(selected, withTime ? dateTimeFormat : dateFormat)
            ) : (
              <span className="leading-6">{buttonLabel}</span>
            )}
          </Button>
        </PopoverTrigger>
        <PopoverContent
          className="w-auto border-none p-0 shadow-[0px_24px_36px_0px_rgba(69,73,77,0.24)]"
          onClick={e => e.stopPropagation()}
        >
          <PopoverClose className="text-primary absolute top-1 right-1 z-[200] cursor-pointer p-2 hover:bg-[#F6F8FA] hover:brightness-100">
            <XIcon className="size-3" />
          </PopoverClose>
          <div className="sm:flex">
            {!isTimeViewActive ? (
              <Calendar
                {...rest}
                mode="single"
                selected={selected || mappedDefaultDate}
                defaultMonth={defaultMonth || mappedDefaultDate}
                disabled={calendarDisabled}
                onSelect={date => {
                  if (!date) return;

                  onSelect(
                    new Date(
                      date.getFullYear(),
                      date.getMonth(),
                      date.getDate(),
                      selected?.getHours() || DEFAULT_HOUR,
                      selected?.getMinutes() || 0
                    )
                  );

                  if (closeOnSelect) return setIsPopoverOpen(false);
                  if (withTime) setIsTimeViewActive(true);
                }}
              />
            ) : (
              <TimePicker
                confirmLabel={confirmLabel}
                handleTimeChange={handleTimeChange}
                selected={selected}
                setOpenedState={setIsPopoverOpen}
                onBackToDateSelection={() => setIsTimeViewActive(false)}
                topLabel={
                  selected
                    ? getTopLabel?.(selected) || format(selected, dateFormat)
                    : ''
                }
              />
            )}
          </div>
          {clearButtonProps?.label && (
            <div
              className={cn(
                'flex justify-end',
                clearButtonProps.containerClassName
              )}
            >
              <Button
                variant="ghost"
                size="sm"
                disabled={!selected || clearButtonProps?.disabled}
                {...clearButtonProps}
                onClick={e => {
                  handleClear();
                  if (closeOnSelect) setIsPopoverOpen(false);
                  clearButtonProps?.onClick?.(e);
                }}
              >
                {clearButtonProps.label}
              </Button>
            </div>
          )}
        </PopoverContent>
      </Popover>
    );
  }
);

export { DatetimePicker };
